import { motion } from "framer-motion";
import { Compass, Mountain, Users, MapPin, ArrowUpRight, MessageCircle } from "lucide-react";
import { Reveal, MaskLines, SectionLabel } from "./Reveal";
import { useLang } from "../i18n";

const ICONS = [Compass, Mountain, Users, MapPin];
const OPEN_MONTHS = new Set([0, 1, 2, 3, 10, 11]); // Jan–Apr, Nov–Dec

export const DesertCall = () => {
  const { t, lang, wa } = useLang();
  const d = t.desertCall;
  return (
    <section id="desert-call" className="relative py-16 md:py-36 bg-[#0B0A08] overflow-hidden" data-testid="desert-call-section">
      <div className="absolute inset-0 grain pointer-events-none opacity-60" />
      <div className="relative max-w-[1440px] mx-auto px-5 md:px-10">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-6 lg:sticky lg:top-28">
            <Reveal><SectionLabel index="00">{d.label}</SectionLabel></Reveal>
            <h2 key={lang} className="mt-8 font-display text-[18vw] sm:text-8xl lg:text-[9rem] leading-[0.85] tracking-tight text-gold-grad uppercase" data-testid="desert-call-title">
              <MaskLines lines={d.title} />
            </h2>
            <Reveal delay={0.1}>
              <p className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2 font-mono text-[11px] sm:text-xs uppercase tracking-[0.3em] text-[#E6C594]">
                <span>{d.season}</span><span className="hidden sm:block w-10 h-px bg-[#D4AF37]/50" /><span>{d.route}</span>
              </p>
            </Reveal>
            <Reveal delay={0.15}><p className="mt-8 text-base text-[#CFC6B6] leading-relaxed max-w-lg">{d.text}</p></Reveal>
            <Reveal delay={0.2}><p className="mt-5 font-display text-2xl sm:text-3xl text-[#F7F4EE]">{d.claim}</p></Reveal>
            <Reveal delay={0.25}>
              <div className="mt-10 flex flex-wrap gap-3">
                <a href="#quote" className="btn-gold" data-testid="desert-call-quote-btn">{d.cta} <ArrowUpRight size={16} /></a>
                <a href={wa} target="_blank" rel="noreferrer" className="btn-ghost" data-testid="desert-call-whatsapp-btn"><MessageCircle size={16} /> {d.ctaWa}</a>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-6 space-y-10">
            <Reveal>
              <motion.div whileHover={{ scale: 1.01 }} transition={{ duration: 0.6 }} className="relative overflow-hidden border border-[#D4AF37]/25 shadow-[0_40px_120px_-40px_rgba(212,175,55,0.35)]">
                <img src="/img/desert_call.jpg" alt="Desert Call 2026/2027 — TSM WAY" className="w-full h-auto block" loading="lazy" data-testid="desert-call-poster" />
              </motion.div>
            </Reveal>

            <div className="grid grid-cols-2 gap-px bg-[#D4AF37]/15 border border-[#D4AF37]/15" data-testid="desert-call-pillars">
              {d.pillars.map(([title, text], i) => {
                const Icon = ICONS[i];
                return (
                  <Reveal key={title} delay={i * 0.06} className="bg-[#0B0A08] p-5 sm:p-7 group hover:bg-[#14120E] transition-colors duration-500">
                    <Icon size={22} strokeWidth={1.4} className="text-[#D4AF37] transition-transform duration-500 group-hover:-translate-y-1" />
                    <p className="mt-5 font-mono text-[11px] uppercase tracking-[0.25em] text-[#F3E5AB]">{title}</p>
                    <p className="mt-2 text-sm text-[#B5AC9C] leading-relaxed">{text}</p>
                  </Reveal>
                );
              })}
            </div>

            <Reveal delay={0.1}>
              <div className="border border-[#D4AF37]/25 bg-[#14120E]/70 backdrop-blur-xl p-6 sm:p-8" data-testid="desert-season">
                <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                  <div>
                    <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#B5AC9C]">{d.seasonLabel}</p>
                    <p className="mt-2 font-display text-3xl sm:text-4xl text-gold-grad" data-testid="desert-season-range">{d.seasonRange}</p>
                  </div>
                  <p className="flex items-center gap-4 font-mono text-[10px] uppercase tracking-[0.2em] text-[#B5AC9C]">
                    <span className="flex items-center gap-2"><span className="w-3 h-3 bg-[#D4AF37]" /> {d.open}</span>
                    <span className="flex items-center gap-2"><span className="w-3 h-3 border border-[#D4AF37]/40" /> {d.closed}</span>
                  </p>
                </div>
                <ol className="mt-6 grid grid-cols-6 sm:grid-cols-12 gap-1.5" data-testid="desert-season-months">
                  {d.months.map((m, i) => {
                    const open = OPEN_MONTHS.has(i);
                    return (
                      <motion.li key={m} initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.04, duration: 0.5 }}
                        className={`flex flex-col items-center gap-2 ${open ? "text-[#0B0A08]" : "text-[#8F877A]"}`} data-testid={`season-month-${i}`} data-open={open}>
                        <span className={`w-full h-10 sm:h-14 ${open ? "bg-[#D4AF37]" : "border border-[#D4AF37]/30"}`} />
                        <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#CFC6B6]">{m}</span>
                      </motion.li>
                    );
                  })}
                </ol>
                <p className="mt-5 text-sm text-[#B5AC9C] leading-relaxed">{d.seasonText}</p>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
